import React from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { BarChart3 } from 'lucide-react';

interface Category {
  category_id: number;
  category_name: string;
  count: number;
}

interface KeywordsFiltersProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  difficultyFilter: string;
  setDifficultyFilter: (filter: string) => void;
  volumeFilter: string;
  setVolumeFilter: (filter: string) => void;
  categoryFilter: string;
  setCategoryFilter: (filter: string) => void;
  categories: Category[];
  totalCount: number;
  selectedKeywordsCount: number;
  onAnalyzeSelected: () => void;
  isAnalyzing: boolean;
  volumeValue: number;
  setVolumeValue: (value: number) => void;
  kdValue: number;
  setKdValue: (value: number) => void;
}

const KeywordsFilters: React.FC<KeywordsFiltersProps> = React.memo(({
  searchTerm,
  setSearchTerm,
  difficultyFilter,
  setDifficultyFilter,
  volumeFilter,
  setVolumeFilter,
  categoryFilter,
  setCategoryFilter,
  categories,
  totalCount,
  selectedKeywordsCount,
  onAnalyzeSelected,
  isAnalyzing,
  volumeValue,
  setVolumeValue,
  kdValue,
  setKdValue,
}) => {
  return (
    <div className="space-y-4 mb-6 px-0">
      {/* Single Row - Analyze Button, Competition Filter, and Sliders */}
      <div className="flex items-center space-x-4">
        {/* Analyze Button */}
        <Button 
          onClick={selectedKeywordsCount === 0 ? undefined : onAnalyzeSelected}
          disabled={isAnalyzing}
          className="relative group whitespace-nowrap"
          title={selectedKeywordsCount === 0 ? "Please select keywords to analyze" : ""}
        >
          {isAnalyzing ? (
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Analyzing...</span>
            </div>
          ) : (
            <>
              <BarChart3 className="mr-0 h-4 w-4" />
              Analyze ({selectedKeywordsCount})
            </>
          )}
          
          {/* Error Tooltip */}
          {selectedKeywordsCount === 0 && (
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-destructive text-destructive-foreground text-sm rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
              Please select keywords to analyze
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-destructive"></div>
            </div>
          )}
        </Button>

        <Select value={difficultyFilter} onValueChange={setDifficultyFilter}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Competition" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="high">High</SelectItem>
          </SelectContent>
        </Select>

        

        {/* KD Slider */}
        <div className="flex items-center space-x-3 min-w-0 w-48">
          <label className="text-sm font-medium text-foreground whitespace-nowrap">KD</label>
          <div className="flex-1 min-w-0">
            <Slider
              value={kdValue}
              onValueChange={setKdValue}
              max={100}
              min={0}
              step={1}
              className="w-full"
            />
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {kdValue}
          </span>
        </div>
        
        {/* Volume Slider */}
        <div className="flex items-center space-x-3 min-w-0 flex-1">
          <label className="text-sm font-medium text-foreground whitespace-nowrap">Volume</label>
          <div className="flex-1 min-w-0">
            <Slider
              value={volumeValue}
              onValueChange={setVolumeValue}
              max={5000000}
              min={0}
              step={10000}
              className="w-full"
            />
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {volumeValue.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Third Row - Category Capsules */}
      {categories && categories.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={() => setCategoryFilter('all')}
            className={`px-2 py-1 text-xs rounded-md transition-colors ${
              categoryFilter === 'all' 
                ? 'bg-primary/20 text-primary' 
                : 'bg-muted/30 text-muted-foreground hover:bg-muted/50 hover:text-foreground'
            }`}
          >
            All ({totalCount})
          </button>
          {categories
            .filter(category => category && category.category_id !== null && category.category_name)
            .map((category) => (
              <button
                key={category.category_id}
                onClick={() => setCategoryFilter(category.category_id.toString())}
                className={`px-2 py-1 text-xs rounded-md transition-colors ${
                  categoryFilter === category.category_id.toString()
                    ? 'bg-primary/20 text-primary'
                    : 'bg-muted/30 text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                }`}
              >
                {category.category_name} ({category.count})
              </button>
            ))}
        </div>
      )}
    </div>
  );
});

KeywordsFilters.displayName = 'KeywordsFilters';

export default KeywordsFilters; 